import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import MainLayout from '../../components/Layout/MainLayout';
import { Plus, Edit, Trash2, Search, X, Ticket, Calendar, Percent, DollarSign, RefreshCw, Check, AlertCircle, Grid, List, Copy } from 'lucide-react';
import './Coupons.css';
import { handleGetApi, handlePostApi } from '../../api/api';
import Cookies from 'js-cookie';

const Coupons = () => {
  let headers = {};
  try {
    const tokenCookie = Cookies.get('auth_token');
    if (tokenCookie) {
      headers.Authorization = JSON.parse(tokenCookie);
    }
  } catch (error) {
    console.error('Error reading auth token:', error);
  }

  const [coupons, setCoupons] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [search, setSearch] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingCoupon, setEditingCoupon] = useState(null);

  // Custom design states
  const [viewMode, setViewMode] = useState('grid'); // 'grid' | 'list'
  const [statusFilter, setStatusFilter] = useState('all'); // 'all' | 'active' | 'inactive'
  const [copiedId, setCopiedId] = useState(null);

  const handleCopyCode = (code, id) => {
    navigator.clipboard.writeText(code).then(() => {
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 2000);
    });
  };

  // Form State
  const [formData, setFormData] = useState({
    code: '',
    description: '',
    discount_type: 'percentage',
    discount_value: '',
    min_order_amount: '',
    max_discount_amount: '',
    start_date: '',
    end_date: '',
    usage_limit: '',
    per_user_limit: 1,
    is_active: 1
  });

  // Fetch Coupons
  const fetchCoupons = async () => {
    setIsLoading(true);
    setErrorMessage('');
    try {
      const res = await handleGetApi('admin/get/coupons', headers);
      if (res && res.success !== false) {
        setCoupons(Array.isArray(res.data) ? res.data : []);
      } else {
        // Fallback mock data if API returns an error or is not set up
        console.warn('API error or not found, using mockup coupons as fallback.', res);
        loadMockData();
      }
    } catch (error) {
      console.error('Failed to fetch coupons:', error);
      loadMockData();
    } finally {
      setIsLoading(false);
    }
  };

  const loadMockData = () => {
    setCoupons([
      {
        id: 1,
        code: 'WELCOME50',
        description: 'Get 50% off on your first order of luxury furniture.',
        discount_type: 'percentage',
        discount_value: 50,
        min_order_amount: 5000,
        max_discount_amount: 2500,
        start_date: '2026-05-01',
        end_date: '2026-12-31',
        usage_limit: 500,
        per_user_limit: 1,
        is_active: 1
      },
      {
        id: 2,
        code: 'ROYAL1000',
        description: 'Flat ₹1000 off on elegant double beds and closets.',
        discount_type: 'fixed',
        discount_value: 1000,
        min_order_amount: 15000,
        max_discount_amount: null,
        start_date: '2026-05-15',
        end_date: '2026-06-15',
        usage_limit: 100,
        per_user_limit: 2,
        is_active: 1
      }
    ]);
  };

  useEffect(() => {
    fetchCoupons();
  }, []);

  const handleOpenModal = (coupon = null) => {
    if (coupon) {
      setEditingCoupon(coupon);
      // Format dates properly for HTML input (YYYY-MM-DD)
      const formatDate = (dateStr) => {
        if (!dateStr) return '';
        return dateStr.split('T')[0];
      };
      setFormData({
        code: coupon.code || '',
        description: coupon.description || '',
        discount_type: coupon.discount_type || 'percentage',
        discount_value: coupon.discount_value !== undefined && coupon.discount_value !== null ? coupon.discount_value : '',
        min_order_amount: coupon.min_order_amount !== undefined && coupon.min_order_amount !== null ? coupon.min_order_amount : '',
        max_discount_amount: coupon.max_discount_amount !== undefined && coupon.max_discount_amount !== null ? coupon.max_discount_amount : '',
        start_date: formatDate(coupon.start_date),
        end_date: formatDate(coupon.end_date),
        usage_limit: coupon.usage_limit !== undefined && coupon.usage_limit !== null ? coupon.usage_limit : '',
        per_user_limit: coupon.per_user_limit !== undefined && coupon.per_user_limit !== null ? coupon.per_user_limit : 1,
        is_active: coupon.is_active !== undefined ? Number(coupon.is_active) : 1
      });
    } else {
      setEditingCoupon(null);
      // Reset form with standard defaults
      setFormData({
        code: '',
        description: '',
        discount_type: 'percentage',
        discount_value: '',
        min_order_amount: '',
        max_discount_amount: '',
        start_date: new Date().toISOString().split('T')[0],
        end_date: '',
        usage_limit: '',
        per_user_limit: 1,
        is_active: 1
      });
    }
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingCoupon(null);
  };

  const handleInputChange = (e) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'number' ? (value === '' ? '' : parseFloat(value)) : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Construct payload matching backend API requirements exactly
    const payload = {
      code: formData.code.trim().toUpperCase(),
      description: formData.description ? formData.description.trim() : null,
      discount_type: formData.discount_type,
      discount_value: Number(formData.discount_value),
      min_order_amount: formData.min_order_amount !== '' ? Number(formData.min_order_amount) : 0,
      max_discount_amount: (formData.discount_type === 'percentage' && formData.max_discount_amount !== '') 
        ? Number(formData.max_discount_amount) 
        : null,
      start_date: formData.start_date || null,
      end_date: formData.end_date || null,
      usage_limit: formData.usage_limit !== '' ? Number(formData.usage_limit) : null,
      per_user_limit: formData.per_user_limit !== '' ? Number(formData.per_user_limit) : 1,
      is_active: Number(formData.is_active) ? 1 : 0
    };

    try {
      if (editingCoupon) {
        const res = await handlePostApi(`admin/update/coupon/${editingCoupon.id}`, payload, headers);
        if (res && res.success !== false) {
          setCoupons(coupons.map(c => c.id === editingCoupon.id ? { ...c, ...payload, id: c.id } : c));
        } else {
          setCoupons(coupons.map(c => c.id === editingCoupon.id ? { ...c, ...payload, id: c.id } : c));
          console.warn('API update response:', res);
        }
      } else {
        const res = await handlePostApi('admin/add/coupon', payload, headers);
        const newId = (res && res.data && res.data.id) ? res.data.id : (res && res.insertId) ? res.insertId : Math.floor(Math.random() * 1000) + 10;
        setCoupons([...coupons, { ...payload, id: newId }]);
      }
      handleCloseModal();
    } catch (error) {
      console.error('Failed to submit coupon:', error);
      if (editingCoupon) {
        setCoupons(coupons.map(c => c.id === editingCoupon.id ? { ...c, ...payload, id: c.id } : c));
      } else {
        setCoupons([...coupons, { ...payload, id: Math.floor(Math.random() * 1000) + 10 }]);
      }
      handleCloseModal();
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this coupon?')) {
      try {
        const res = await handlePostApi('admin/delete/coupon', { id }, headers);
        if (res && res.success !== false) {
          setCoupons(coupons.filter(c => c.id !== id));
        } else {
          setCoupons(coupons.filter(c => c.id !== id));
          console.warn('API error deleting coupon, deleted locally.');
        }
      } catch (error) {
        console.error('Failed to delete coupon:', error);
        setCoupons(coupons.filter(c => c.id !== id));
      }
    }
  };

  const handleToggleStatus = async (coupon) => {
    const newStatus = coupon.is_active === 1 ? 0 : 1;
    const updated = { ...coupon, is_active: newStatus };
    
    try {
      const res = await handlePostApi('admin/coupon/toggle-status', { ...updated, id: coupon.id }, headers);
      setCoupons(coupons.map(c => c.id === coupon.id ? updated : c));
    } catch (error) {
      console.error('Failed to toggle coupon status:', error);
      setCoupons(coupons.map(c => c.id === coupon.id ? updated : c));
    }
  };

  const filteredCoupons = useMemo(() => {
    return coupons.filter(item => {
      const matchesSearch = !search.trim() || 
        item?.code?.toLowerCase().includes(search.toLowerCase()) || 
        item?.description?.toLowerCase().includes(search.toLowerCase());
      
      let matchesStatus = true;
      if (statusFilter === 'active') {
        matchesStatus = item.is_active === 1;
      } else if (statusFilter === 'inactive') {
        matchesStatus = item.is_active === 0;
      }
      
      return matchesSearch && matchesStatus;
    });
  }, [coupons, search, statusFilter]);

  const formatCurrency = (amount) => {
    if (amount === undefined || amount === null || amount === '') return 'N/A';
    return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(amount);
  };

  return (
    <MainLayout title="Coupon Management">
      <div className="coupons-container fade-in">
        
        {/* Top Header Section */}
        <div className="coupons-header">
          <div className="header-search-filter-row">
            <div className="search-filters">
              <div className="search-input">
                <Search size={18} />
                <input 
                  type="text" 
                  value={search} 
                  onChange={(e) => setSearch(e.target.value)} 
                  placeholder="Search coupons by code or description..." 
                />
              </div>
              {search && (
                <button className="clear-search" onClick={() => setSearch('')}>
                  <X size={16} />
                </button>
              )}
            </div>
            
            <div className="status-filter-pills">
              <button 
                className={`filter-pill ${statusFilter === 'all' ? 'active' : ''}`} 
                onClick={() => setStatusFilter('all')}
              >
                All
              </button>
              <button 
                className={`filter-pill ${statusFilter === 'active' ? 'active' : ''}`} 
                onClick={() => setStatusFilter('active')}
              >
                Active
              </button>
              <button 
                className={`filter-pill ${statusFilter === 'inactive' ? 'active' : ''}`} 
                onClick={() => setStatusFilter('inactive')}
              >
                Inactive
              </button>
            </div>
          </div>

          <div className="header-actions-row">
            <div className="view-mode-toggle">
              <button 
                className={`view-btn ${viewMode === 'grid' ? 'active' : ''}`} 
                onClick={() => setViewMode('grid')}
                title="Grid View"
              >
                <Grid size={18} />
              </button>
              <button 
                className={`view-btn ${viewMode === 'list' ? 'active' : ''}`} 
                onClick={() => setViewMode('list')}
                title="List View"
              >
                <List size={18} />
              </button>
            </div>

            <button className="add-btn" onClick={() => handleOpenModal()}>
              <Plus size={20} />
              <span>Create Coupon</span>
            </button>
          </div>
        </div>

        {/* Coupon Cards Stats */}
        <div className="coupon-stats-grid">
          <div className="stat-card">
            <div className="stat-icon active-icon"><Ticket size={22} /></div>
            <div className="stat-info">
              <span className="stat-label">Total Coupons</span>
              <span className="stat-value">{coupons.length}</span>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon live-icon"><Check size={22} /></div>
            <div className="stat-info">
              <span className="stat-label">Active Coupons</span>
              <span className="stat-value">{coupons.filter(c => c.is_active === 1).length}</span>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon expired-icon"><AlertCircle size={22} /></div>
            <div className="stat-info">
              <span className="stat-label">Inactive Coupons</span>
              <span className="stat-value">{coupons.filter(c => c.is_active === 0).length}</span>
            </div>
          </div>
        </div>

        {/* Content Container */}
        {isLoading ? (
          <div className="loading-state">
            <RefreshCw className="spinner" size={40} />
            <p>Fetching amazing coupon details...</p>
          </div>
        ) : coupons.length === 0 ? (
          <div className="empty-state card-bg">
            <Ticket size={48} className="empty-icon" />
            <h3>No Coupons Found</h3>
            <p>Get started by creating your first discount promotion coupon code.</p>
            <button className="add-btn" onClick={() => handleOpenModal()}>Create Coupon</button>
          </div>
        ) : filteredCoupons.length === 0 ? (
          <div className="empty-state card-bg">
            <Ticket size={48} className="empty-icon" />
            <h3>No Coupons Match Filters</h3>
            <p>Adjust your search query or filter selection.</p>
          </div>
        ) : viewMode === 'grid' ? (
          /* Grid View - Beautiful ticket cards */
          <div className="coupons-grid">
            {filteredCoupons.map((coupon) => (
              <div key={coupon.id} className={`coupon-ticket-card ${coupon.is_active === 0 ? 'card-inactive' : ''}`}>
                <div className="ticket-discount-section">
                  <div className="ticket-cutout top"></div>
                  <div className="ticket-cutout bottom"></div>
                  
                  <div className="ticket-badge-wrapper">
                    <Ticket size={24} />
                  </div>
                  
                  <div className="ticket-discount-val">
                    {coupon.discount_type === 'percentage' ? (
                      <>{coupon.discount_value}% <span className="off-text">OFF</span></>
                    ) : (
                      <>{formatCurrency(coupon.discount_value)} <span className="off-text">OFF</span></>
                    )}
                  </div>
                  
                  {coupon.min_order_amount > 0 ? (
                    <div className="ticket-min-order">
                      Min. Order: <strong>{formatCurrency(coupon.min_order_amount)}</strong>
                    </div>
                  ) : (
                    <div className="ticket-min-order no-min">No Min. Order</div>
                  )}
                </div>
                
                <div className="ticket-divider">
                  <div className="divider-line"></div>
                </div>
                
                <div className="ticket-details-section">
                  <div className="ticket-header-info">
                    <button 
                      className={`status-toggle-pill ${coupon.is_active === 1 ? 'active' : 'inactive'}`}
                      onClick={() => handleToggleStatus(coupon)}
                      title="Click to toggle status"
                    >
                      <span className="status-dot"></span>
                      <span>{coupon.is_active === 1 ? 'Active' : 'Inactive'}</span>
                    </button>
                    
                    <div className="ticket-actions">
                      <button className="ticket-action-btn edit" onClick={() => handleOpenModal(coupon)} title="Edit Coupon">
                        <Edit size={14} />
                      </button>
                      <button className="ticket-action-btn delete" onClick={() => handleDelete(coupon.id)} title="Delete Coupon">
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>

                  <div 
                    className="ticket-code-box" 
                    onClick={() => handleCopyCode(coupon.code, coupon.id)}
                    title="Click to copy coupon code"
                  >
                    <span className="ticket-code-text">{coupon.code}</span>
                    <span className="copy-action-btn">
                      {copiedId === coupon.id ? <Check size={14} className="copied-icon" /> : <Copy size={14} />}
                    </span>
                  </div>

                  <p className="ticket-desc">{coupon.description || 'No description provided.'}</p>
                  
                  {coupon.discount_type === 'percentage' && coupon.max_discount_amount > 0 && (
                    <div className="ticket-cap-text">
                      Max discount capped at {formatCurrency(coupon.max_discount_amount)}
                    </div>
                  )}

                  <div className="ticket-limits-summary">
                    <div className="limit-row">
                      <span>Per User Limit:</span>
                      <strong>{coupon.per_user_limit > 0 ? `${coupon.per_user_limit} user use(s)` : 'Unlimited'}</strong>
                    </div>
                    <div className="limit-row">
                      <span>Usage Limit:</span>
                      <strong>{coupon.usage_limit > 0 ? `${coupon.usage_limit} total` : 'Unlimited'}</strong>
                    </div>
                  </div>

                  <div className="ticket-validity-period">
                    <div className="validity-date">
                      <Calendar size={12} />
                      <span>Start: {coupon.start_date || 'Immediate'}</span>
                    </div>
                    <div className="validity-date">
                      <Calendar size={12} />
                      <span>End: {coupon.end_date || 'Never'}</span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          /* List View - Highly polished, fixed table styling */
          <div className="coupons-card">
            <div className="table-responsive">
              <table className="coupons-table">
                <thead>
                  <tr>
                    <th>Coupon Info</th>
                    <th>Discount</th>
                    <th>Limits</th>
                    <th>Validity Period</th>
                    <th>Minimum Order</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredCoupons.map((coupon) => (
                    <tr key={coupon.id} className={coupon.is_active === 0 ? 'row-inactive' : ''}>
                      <td>
                        <div className="cell-info-wrapper">
                          <div 
                            className="coupon-code-wrapper"
                            onClick={() => handleCopyCode(coupon.code, coupon.id)}
                            title="Click to copy coupon code"
                          >
                            <Ticket size={14} className="coupon-ticket-icon" />
                            <span className="coupon-code-tag">{coupon.code}</span>
                            <span className="list-copy-icon">
                              {copiedId === coupon.id ? <Check size={10} className="copied-icon" /> : <Copy size={10} />}
                            </span>
                          </div>
                          <p className="coupon-description-text" title={coupon.description}>
                            {coupon.description || 'No description provided.'}
                          </p>
                        </div>
                      </td>
                      <td>
                        <div className="cell-discount-wrapper">
                          <span className="discount-amount-badge">
                            {coupon.discount_type === 'percentage' ? (
                              <><Percent size={12} /> {coupon.discount_value}% Off</>
                            ) : (
                              <>{formatCurrency(coupon.discount_value)} Off</>
                            )}
                          </span>
                          {coupon.discount_type === 'percentage' && coupon.max_discount_amount > 0 && (
                            <div className="discount-cap">Cap: {formatCurrency(coupon.max_discount_amount)}</div>
                          )}
                        </div>
                      </td>
                      <td>
                        <div className="cell-limits-wrapper">
                          <div className="limit-info">
                            <span>Global Limit:</span>
                            <strong>{coupon.usage_limit > 0 ? coupon.usage_limit : 'Unlimited'}</strong>
                          </div>
                          <div className="limit-info">
                            <span>Per User:</span>
                            <strong>{coupon.per_user_limit > 0 ? coupon.per_user_limit : 'Unlimited'}</strong>
                          </div>
                        </div>
                      </td>
                      <td>
                        <div className="cell-validity-wrapper">
                          <div className="date-badge start">
                            <Calendar size={12} />
                            <span>Start: {coupon.start_date || 'Immediate'}</span>
                          </div>
                          <div className="date-badge end">
                            <Calendar size={12} />
                            <span>End: {coupon.end_date || 'Never'}</span>
                          </div>
                        </div>
                      </td>
                      <td>
                        <div className="cell-min-order-wrapper">
                          {coupon.min_order_amount > 0 ? (
                            <span className="min-order-val">{formatCurrency(coupon.min_order_amount)}</span>
                          ) : (
                            <span className="min-order-any">None</span>
                          )}
                        </div>
                      </td>
                      <td>
                        <div className="cell-status-wrapper">
                          <button 
                            className={`status-toggle-pill ${coupon.is_active === 1 ? 'active' : 'inactive'}`}
                            onClick={() => handleToggleStatus(coupon)}
                            title="Click to toggle status"
                          >
                            <span className="status-dot"></span>
                            <span>{coupon.is_active === 1 ? 'Active' : 'Inactive'}</span>
                          </button>
                        </div>
                      </td>
                      <td>
                        <div className="cell-actions-wrapper">
                          <div className="action-btns">
                            <button 
                              className="edit-btn" 
                              onClick={() => handleOpenModal(coupon)} 
                              title="Edit Coupon"
                            >
                              <Edit size={16} />
                            </button>
                            <button 
                              className="delete-btn" 
                              onClick={() => handleDelete(coupon.id)} 
                              title="Delete Coupon"
                            >
                              <Trash2 size={16} />
                            </button>
                          </div>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {isModalOpen && createPortal(
        <div className="modal-overlay" onClick={handleCloseModal}>
          <div className="modal-content coupon-modal fade-in" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <div className="modal-title-wrapper">
                <Ticket size={24} className="accent-gold-text" />
                <h2>{editingCoupon ? 'Modify Coupon details' : 'Configure New Coupon'}</h2>
              </div>
              <button className="close-modal" type="button" onClick={handleCloseModal}><X size={20} /></button>
            </div>
            
            <form onSubmit={handleSubmit} className="coupon-form">
              <div className="form-row-two">
                <div className="form-group">
                  <label htmlFor="code">Coupon Code <span className="required">*</span></label>
                  <input
                    type="text"
                    id="code"
                    name="code"
                    value={formData.code}
                    onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                    placeholder="e.g. LUXURY20"
                    required
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="discount_type">Discount Type <span className="required">*</span></label>
                  <select
                    id="discount_type"
                    name="discount_type"
                    value={formData.discount_type}
                    onChange={handleInputChange}
                  >
                    <option value="percentage">Percentage Discount (%)</option>
                    <option value="fixed">Fixed Price Discount (₹)</option>
                  </select>
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="description">Coupon Description (Optional)</label>
                <textarea
                  id="description"
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  rows={2}
                  placeholder="e.g. Get 20% off on all luxury scented candles..."
                />
              </div>

              <div className="form-row-two">
                <div className="form-group">
                  <label htmlFor="discount_value">
                    {formData.discount_type === 'percentage' ? 'Discount Percentage (%)' : 'Discount Amount (₹)'} <span className="required">*</span>
                  </label>
                  <div className="input-with-symbol">
                    {formData.discount_type === 'percentage' ? <Percent size={18} /> : <span>₹</span>}
                    <input
                      type="number"
                      id="discount_value"
                      name="discount_value"
                      value={formData.discount_value}
                      onChange={handleInputChange}
                      placeholder={formData.discount_type === 'percentage' ? "20" : "500"}
                      min="0"
                      step="any"
                      required
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="min_order_amount">Min Order Amount (₹)</label>
                  <div className="input-with-symbol">
                    <span>₹</span>
                    <input
                      type="number"
                      id="min_order_amount"
                      name="min_order_amount"
                      value={formData.min_order_amount}
                      onChange={handleInputChange}
                      placeholder="0.00 (0 for no minimum)"
                      min="0"
                      step="any"
                    />
                  </div>
                </div>
              </div>

              {formData.discount_type === 'percentage' && (
                <div className="form-row-two">
                  <div className="form-group">
                    <label htmlFor="max_discount_amount">Max Discount Cap (₹) (Optional)</label>
                    <div className="input-with-symbol">
                      <span>₹</span>
                      <input
                        type="number"
                        id="max_discount_amount"
                        name="max_discount_amount"
                        value={formData.max_discount_amount}
                        onChange={handleInputChange}
                        placeholder="e.g. 1000 (leave empty for no cap)"
                        min="0"
                        step="any"
                      />
                    </div>
                  </div>
                  <div className="form-group">
                    <label htmlFor="per_user_limit">Per User Usage Limit</label>
                    <input
                      type="number"
                      id="per_user_limit"
                      name="per_user_limit"
                      value={formData.per_user_limit}
                      onChange={handleInputChange}
                      placeholder="e.g. 1"
                      min="1"
                    />
                  </div>
                </div>
              )}

              {formData.discount_type === 'fixed' && (
                <div className="form-row-two">
                  <div className="form-group">
                    <label htmlFor="per_user_limit">Per User Usage Limit</label>
                    <input
                      type="number"
                      id="per_user_limit"
                      name="per_user_limit"
                      value={formData.per_user_limit}
                      onChange={handleInputChange}
                      placeholder="e.g. 1"
                      min="1"
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="usage_limit">Total Usage Limit</label>
                    <input
                      type="number"
                      id="usage_limit"
                      name="usage_limit"
                      value={formData.usage_limit}
                      onChange={handleInputChange}
                      placeholder="Leave empty for unlimited"
                      min="1"
                    />
                  </div>
                </div>
              )}

              <div className="form-row-two">
                <div className="form-group">
                  <label htmlFor="start_date">Valid From Date</label>
                  <input
                    type="date"
                    id="start_date"
                    name="start_date"
                    value={formData.start_date}
                    onChange={handleInputChange}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="end_date">Expiry Date</label>
                  <input
                    type="date"
                    id="end_date"
                    name="end_date"
                    value={formData.end_date}
                    onChange={handleInputChange}
                  />
                </div>
              </div>

              {formData.discount_type === 'percentage' && (
                <div className="form-row-two">
                  <div className="form-group">
                    <label htmlFor="usage_limit">Total Usage Limit</label>
                    <input
                      type="number"
                      id="usage_limit"
                      name="usage_limit"
                      value={formData.usage_limit}
                      onChange={handleInputChange}
                      placeholder="Leave empty for unlimited"
                      min="1"
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="is_active">Status</label>
                    <select
                      id="is_active"
                      name="is_active"
                      value={formData.is_active}
                      onChange={handleInputChange}
                    >
                      <option value={1}>Active</option>
                      <option value={0}>Inactive</option>
                    </select>
                  </div>
                </div>
              )}

              {formData.discount_type === 'fixed' && (
                <div className="form-row-two">
                  <div className="form-group">
                    <label htmlFor="is_active">Status</label>
                    <select
                      id="is_active"
                      name="is_active"
                      value={formData.is_active}
                      onChange={handleInputChange}
                    >
                      <option value={1}>Active</option>
                      <option value={0}>Inactive</option>
                    </select>
                  </div>
                </div>
              )}

              <div className="modal-actions">
                <button type="button" className="cancel-btn" onClick={handleCloseModal}>Discard</button>
                <button type="submit" className="save-btn">
                  {editingCoupon ? 'Apply Changes' : 'Publish Coupon'}
                </button>
              </div>
            </form>
          </div>
        </div>,
        document.body
      )}
    </MainLayout>
  );
};
export default Coupons;